import { NavigateFunction } from 'react-router-dom';

import { createAsyncThunk } from '@reduxjs/toolkit';

import { BinType } from 'commons/enums';
import { PRIMARY_BLUE, PRIMARY_RED } from 'commons/styles/colors';
import { IBin, STOItem } from 'commons/types';
import { defaultActionProcess } from 'commons/utils/defaultActionProcess';
import {
  addAssignedSTOReceiveItem,
  removeAssignedSTOReceiveItem,
  setAssignedSTOReceiveItem,
  setSTOItemReceiveActual,
  setSTOReceiveBinRecommendationInfo,
} from 'redux-stores/reducers/stoReceiveItemReducer';
import { snackbarSetData } from 'redux-stores/reducers/utilityReducer';
import { AppDispatch } from 'redux-stores/store';
import { STO_RECEIVE_ROUTES } from 'routes/constants';
import { ISTOProductDetailRequest } from 'services/apis/SwipeRxWmsApiStoPickItem/types';
import { swipeRxWmsApiStoReceiveItem } from 'services/apis/SwipeRxWmsApiStoReceiveItem';
import {
  ISTOReceivedItemRequest,
  ISTOCompleteReceiveItem,
} from 'services/apis/SwipeRxWmsApiStoReceiveItem/type';
import { STOReceiveItemAssignByBoxPalletRequest } from 'services/apis/SwipeRxWmsApiStoReceiveItem/type/sto-receive-assign-remove-by-box-request.type';

export const getSTOReceiveAssignedItems = createAsyncThunk(
  'stoReceive/getSTOReceiveItemsAction',
  async (_, { dispatch }) => {
    defaultActionProcess(async () => {
      const data = await swipeRxWmsApiStoReceiveItem.getSTOReceiveItems();
      dispatch(setAssignedSTOReceiveItem(data));
    }, dispatch);
    return undefined;
  },
);

export const getSTOReceiveItemsAction = createAsyncThunk(
  'stoReceive/getSTOReceiveItemsAction',
  async (_, { dispatch }) => {
    defaultActionProcess(async () => {
      const data = await swipeRxWmsApiStoReceiveItem.getSTOReceiveList();
      dispatch(setAssignedSTOReceiveItem(data));
    }, dispatch);
    return undefined;
  },
);

export const actionSetAssignedSTOReceiveItem = createAsyncThunk(
  'stoReceive/actionSetAssignedSTOReceiveItem',
  async (_, { dispatch }) => {
    defaultActionProcess(async () => {
      const data = await swipeRxWmsApiStoReceiveItem.assignSTOReceiveItem();
      dispatch(setAssignedSTOReceiveItem(data));
    }, dispatch);
    return undefined;
  },
);

export const actionSetAssignedSTOReceiveItemByBoxPallet = createAsyncThunk(
  'stoReceive/actionSetAssignedSTOReceiveItemByBoxPallet',
  async (params: STOReceiveItemAssignByBoxPalletRequest, { dispatch }) => {
    defaultActionProcess(async () => {
      const data =
        await swipeRxWmsApiStoReceiveItem.assignSTOReceiveItemByBoxPallet(
          params,
        );
      dispatch(setAssignedSTOReceiveItem(data));
    }, dispatch);
    return undefined;
  },
);

export const actionAddAssignedSTOReceiveItem = createAsyncThunk(
  'stoReceive/actionAddAssignedSTOReceiveItem',
  async (_, { dispatch }) => {
    await defaultActionProcess(async () => {
      const data = await swipeRxWmsApiStoReceiveItem.addSTOReceiveItem();
      dispatch(addAssignedSTOReceiveItem(data));
    }, dispatch);
    return undefined;
  },
);

export const actionRemoveAssignedSTOReceiveItem = createAsyncThunk(
  'stoReceive/actionRemoveAssignedSTOReceiveItem',
  async (params: { stoNumber: string; skuCode: string }, { dispatch }) => {
    defaultActionProcess(async () => {
      const { stoNumber, skuCode } = params;
      const data = await swipeRxWmsApiStoReceiveItem.removeSTOReceiveItem({
        sto_number: stoNumber,
        sku_code: skuCode,
      });
      dispatch(removeAssignedSTOReceiveItem(data));
    }, dispatch);
    return undefined;
  },
);

export const actionRemoveAssignedSTOReceiveItemByBoxPallet = createAsyncThunk(
  'stoReceive/actionRemoveAssignedSTOReceiveItemByBoxPallet',
  async (params: STOReceiveItemAssignByBoxPalletRequest, { dispatch }) => {
    defaultActionProcess(async () => {
      await swipeRxWmsApiStoReceiveItem.removeSTOReceiveItemByBoxPallet(params);
      dispatch(setAssignedSTOReceiveItem([]));
    }, dispatch);
    return undefined;
  },
);

export const actionStartSTOReceiveItem = createAsyncThunk(
  'stoReceive/actionStartSTOReceiveItem',
  async (
    payload: { items: STOItem[]; navigate: NavigateFunction },
    { dispatch },
  ) => {
    defaultActionProcess(async () => {
      const data = await swipeRxWmsApiStoReceiveItem.startReceivingProcess(
        payload.items.map((item) => {
          return { sto_number: item.sto_number, sku_code: item.sku_code };
        }),
      );
      if (data !== 'ok') {
        dispatch(
          snackbarSetData({
            open: true,
            message: data,
            color: PRIMARY_BLUE,
          }),
        );
      }
      payload.navigate(STO_RECEIVE_ROUTES.LIST);
    }, dispatch);
    return undefined;
  },
);

export const getSTOReceiveProductDetail = async (
  params: ISTOProductDetailRequest,
  dispatch: AppDispatch,
): Promise<IBin | null> => {
  const response = await swipeRxWmsApiStoReceiveItem.getProductDetail(params);
  if (response && response.data) {
    return response.data;
  }
  if (response.error) {
    const errorMessage =
      response.error === true
        ? 'Failed to get product detail. Please make sure SKU, Bin, and Inventory Number is correct.'
        : response.error;
    dispatch(
      snackbarSetData({
        open: true,
        message: errorMessage,
        color: PRIMARY_RED,
      }),
    );
  }
  return null;
};

export const postSTOReceiveItem = async (
  params: ISTOReceivedItemRequest,
  dispatch: AppDispatch,
): Promise<void> => {
  const response = await swipeRxWmsApiStoReceiveItem.postReceiveItem(params);
  if (!response) {
    dispatch(
      snackbarSetData({
        open: true,
        message:
          'Failed to get pick item. Please make sure SKU, Bin, Inventory Number, and Quantity is correct.',
        color: PRIMARY_RED,
      }),
    );
  } else {
    dispatch(
      setSTOItemReceiveActual({
        isRemove: false,
        item: params.item,
        itemIdx: params.item_idx,
      }),
    );
  }
};

export const postSTOReceiveRemoveBatch = async (
  params: ISTOReceivedItemRequest,
  dispatch: AppDispatch,
): Promise<void> => {
  defaultActionProcess(
    async () => {
      await swipeRxWmsApiStoReceiveItem.postRemoveBatch({
        sku_code: params.sku_code,
        sto_number: params.sto_number,
        bin_number: params.item.bin,
        inventory_number: params.item.inventory_number,
      });

      dispatch(
        setSTOItemReceiveActual({
          isRemove: true,
          item: params.item,
          itemIdx: params.item_idx,
        }),
      );
    },
    dispatch,
    false,
  );
};

export const getSTOReceiveItemBinRecommendationInfo = createAsyncThunk(
  'stoReceive/getSTOReceiveItemBinRecommendationInfo',
  async (params: { skuCode: string; binType: BinType }, { dispatch }) => {
    const { skuCode, binType } = params;
    defaultActionProcess(async () => {
      const result = await swipeRxWmsApiStoReceiveItem.getBinRecommendationInfo(
        skuCode,
        binType,
      );
      dispatch(setSTOReceiveBinRecommendationInfo(result));
    }, dispatch);
  },
);

export const completeSTOReceiveAction = createAsyncThunk(
  'stoReceive/completestoReceiveAction',
  async (
    params: { payload: ISTOCompleteReceiveItem; postAction: () => void },
    { dispatch },
  ) => {
    const { payload, postAction } = params;
    defaultActionProcess(async () => {
      await swipeRxWmsApiStoReceiveItem.postCompleteReceiving(payload);
      postAction();
    }, dispatch);
  },
);
